import React from 'react';
// import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import {
  Box,
  Typography,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Grid,
  Card,
  CardContent,
  Button,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
const zocdocLink =
  'https://www.zocdoc.com/practice/aspire-bhp-179924?lock=true&isNewPatient=false&referrerType=widget';
const faqSections = [
  {
    title: 'Scheduling & Appointments',
    questions: [
      {
        question: 'How do I schedule an appointment?',
        answer:
          'You can request an appointment online through our scheduling page or call Aspire Behavioral Health directly at 210-866-9850.',
      },
      {
        question: 'Do you offer in-person appointments?',
        answer:
          'Yes. Aspire Behavioral Health offers in-person appointments in San Antonio.',
      },
      {
        question: 'Do you offer telehealth appointments?',
        answer:
          'Yes. We offer secure telehealth psychiatry appointments for patients throughout Texas and Minnesota, including San Antonio and surrounding areas.',
      },
      {
        question: 'What should I bring to my first appointment?',
        answer:
          'Please bring a valid ID, insurance card, current medication list, and any relevant medical or mental health records if available.',
      },
      {
        question: 'What happens during the first appointment?',
        answer:
          'Your provider will complete a psychiatric evaluation, review your symptoms and history, discuss your goals, and create a personalized treatment plan.',
      },
    ],
  },
  {
    title: 'Insurance & Payments',
    questions: [
      {
        question: 'Do you accept insurance?',
        answer:
          'Yes. Aspire Behavioral Health accepts many major insurance plans for psychiatric care, medication management, ADHD testing, and other mental health services.',
      },
      {
        question: 'What insurance plans do you accept?',
        answer:
          'We accept many major insurance providers including Aetna, Ambetter, Anthem, Blue Cross and Blue Shield, Humana, Magellan, Medicare, Molina Healthcare, Optum, Oscar Health, Curative, TRICARE, UnitedHealthcare, UHC, UBH, and WellCare.',
      },
      {
        question: 'What if my insurance is not listed?',
        answer:
          'Please call our office. We may still be able to verify your coverage or discuss available payment options.',
      },
      {
        question: 'Are telehealth appointments covered by insurance?',
        answer:
          'Many insurance plans cover telehealth psychiatry appointments, but coverage varies by provider, plan, location, and service.',
      },
    ],
  },
  {
    title: 'Services We Offer',
    questions: [
      {
        question: 'What mental health conditions do you treat?',
        answer:
          'We treat anxiety, depression, ADHD, bipolar disorder, PTSD, mood disorders, OCD, opioid use disorder, and other mental health concerns.',
      },
      {
        question: 'Do you provide medication management?',
        answer:
          'Yes. Our providers offer psychiatric evaluations and medication management to help monitor symptoms, treatment progress, and medication effectiveness.',
      },
      {
        question: 'Do you offer ADHD testing?',
        answer:
          'Yes. Aspire Behavioral Health offers ADHD testing and assessments, including QbTest® and Creyos assessments.',
      },
      {
        question: 'Do you offer TMS therapy?',
        answer:
          'Yes. Aspire Behavioral Health offers Transcranial Magnetic Stimulation, or TMS therapy, for eligible patients with depression.',
      },
      {
        question: 'Do you offer SPRAVATO® treatment?',
        answer:
          'Yes. SPRAVATO® is available for eligible adults with treatment-resistant depression and is provided in a monitored clinical setting.',
      },
      {
        question: 'Do you offer Suboxone® treatment?',
        answer:
          'Yes. We offer Suboxone® treatment for opioid use disorder as part of medication-assisted treatment.',
      },
    ],
  },
];

const FAQPage = () => {
  return (
    <>
      <Helmet>
        <title>FAQ | Aspire Behavioral Health</title>
        <meta
          name="description"
          content="Frequently asked questions about scheduling, appointments, insurance, psychiatric care, medication management, ADHD testing, telehealth psychiatry, TMS therapy, SPRAVATO, and Suboxone treatment at Aspire Behavioral Health in San Antonio, Texas."
        />
      </Helmet>

      <Box
        sx={{
          backgroundColor: '#f9faf7',
          minHeight: '100vh',
          py: { xs: 6, md: 8 },
          px: { xs: 2, sm: 3, md: 5 },
          width: '100%',
          maxWidth: '100vw',
          overflowX: 'hidden',
          boxSizing: 'border-box',
        }}
      >
        <Box sx={{ maxWidth: '1100px', mx: 'auto' }}>
          <Typography
            variant="h3"
            sx={{
              textAlign: 'center',
              fontWeight: 700,
              color: '#2e3a11',
              mb: 2,
              fontSize: { xs: '2.2rem', md: '3rem' },
            }}
          >
            Frequently Asked Questions
          </Typography>

          <Typography
            variant="body1"
            sx={{
              textAlign: 'center',
              maxWidth: '850px',
              mx: 'auto',
              lineHeight: 1.9,
              color: '#4b4b4b',
              mb: 7,
              fontSize: { xs: '1rem', sm: '1.1rem' },
            }}
          >
            Find answers about scheduling appointments, accepted insurance,
            telehealth psychiatry, medication management, ADHD testing, TMS
            therapy, SPRAVATO®, Suboxone® treatment, and mental health services
            at Aspire Behavioral Health in San Antonio, Texas.
          </Typography>

          <Grid container spacing={4}>
            {faqSections.map((section) => (
              <Grid item xs={12} key={section.title}>
                <Card
                  sx={{
                    borderRadius: 4,
                    backgroundColor: '#eef3e6',
                    border: '1px solid rgba(58,76,23,0.10)',
                    boxShadow: '0px 4px 12px rgba(0,0,0,0.06)',
                  }}
                >
                  <CardContent sx={{ p: { xs: 3, md: 4 } }}>
                    <Typography
                      variant="h4"
                      sx={{
                        fontWeight: 700,
                        color: '#2e3a11',
                        mb: 3,
                        fontSize: { xs: '1.8rem', md: '2.2rem' },
                      }}
                    >
                      {section.title}
                    </Typography>

                    {section.questions.map((faq) => (
                      <Accordion
                        key={faq.question}
                        sx={{
                          mb: 2,
                          borderRadius: '14px !important',
                          backgroundColor: 'white',
                          boxShadow: '0px 3px 10px rgba(0,0,0,0.04)',
                          '&:before': {
                            display: 'none',
                          },
                        }}
                      >
                        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                          <Typography
                            sx={{
                              fontWeight: 700,
                              color: '#2e3a11',
                              textAlign: 'left',
                            }}
                          >
                            {faq.question}
                          </Typography>
                        </AccordionSummary>

                        <AccordionDetails>
                          <Typography
                            sx={{
                              color: '#4b4b4b',
                              lineHeight: 1.8,
                              textAlign: 'left',
                            }}
                          >
                            {faq.answer}
                          </Typography>
                        </AccordionDetails>
                      </Accordion>
                    ))}
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>

          <Box sx={{ textAlign: 'center', mt: { xs: 7, md: 9 } }}>
            <Typography
              variant="h4"
              sx={{
                fontWeight: 700,
                color: '#2e3a11',
                mb: 2,
              }}
            >
              Still Have Questions?
            </Typography>

            <Typography
              variant="body1"
              sx={{
                maxWidth: '650px',
                mx: 'auto',
                mb: 4,
                lineHeight: 1.8,
                color: '#4b4b4b',
              }}
            >
              Contact Aspire Behavioral Health to learn more about appointments,
              insurance coverage, telehealth psychiatry, ADHD testing,
              medication management, and mental health treatment options.
            </Typography>

            <Box
              sx={{
                display: 'flex',
                gap: 2,
                justifyContent: 'center',
                flexWrap: 'wrap',
              }}
            >
              <Button
                variant="contained"
                href="tel:2108669850"
                sx={primaryButtonStyle}
              >
                Call 210-866-9850
              </Button>

              <Button
                component="a"
                href={zocdocLink}
                target="_blank"
                rel="noopener noreferrer"
                variant="outlined"
                sx={secondaryButtonStyle}
              >
                Schedule an Appointment
              </Button>
            </Box>
          </Box>
        </Box>
      </Box>
    </>
  );
};

const primaryButtonStyle = {
  backgroundColor: '#dfe8d3',
  color: '#2e3a11',
  fontWeight: 700,
  px: 4,
  py: 1.3,
  borderRadius: '30px',
  textTransform: 'none',
  boxShadow: 'none',
  '&:hover': {
    backgroundColor: '#d3dfc3',
    boxShadow: 'none',
  },
};

const secondaryButtonStyle = {
  backgroundColor: 'transparent',
  border: '2px solid #3a4c17',
  color: '#3a4c17 !important',
  fontWeight: 700,
  px: 4,
  py: 1.3,
  borderRadius: '30px',
  textTransform: 'none',
  '&:visited': {
    color: '#3a4c17',
  },
  '&:hover': {
    backgroundColor: 'rgba(58,76,23,0.05)',
    border: '2px solid #3a4c17',
    color: '#3a4c17',
  },
};

export default FAQPage;